import { useState } from 'react';
import { useParams } from 'react-router';
import {
  ORG_ROLES,
  ORG_ROLE_DESCRIPTIONS,
  ORG_ROLE_LABELS,
  usableOrgCapabilities,
  type Membership,
  type OrgCapability,
  type OrgRole,
} from '@gatecheckpro/contracts';
import {
  Button,
  Card,
  Field,
  LoadingRegion,
  MEMBERSHIP_STATES,
  Notice,
  Status,
  presentation,
} from '@gatecheckpro/design-system';
import { api } from '../lib/api.ts';
import { useResource } from '../lib/useResource.ts';
import { FailureNotice } from '../lib/refusal.tsx';
import { useViewer } from '../lib/session.tsx';

/**
 * The team page.
 *
 * Roles are shown with what they mean, not as enum names. Somebody granting
 * `FINANCE_APPROVER` at speed on a Friday should be able to read what they are
 * handing over without leaving the page.
 */
export function TeamPage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const viewer = useViewer();
  const base = `/api/v1/organizer/organizations/${organizationId ?? ''}`;
  const { resource, reload } = useResource<{ members: Membership[] }>(
    organizationId === undefined ? null : `${base}/members`,
  );

  const held = (viewer.organizations.find(
    (candidate) => candidate.organizationId === organizationId,
  )?.capabilities ?? []) as OrgCapability[];
  // What the actor holds, and what this session can actually exercise. Managing
  // members needs a second factor, so an owner on an unverified session sees the
  // team and is told why they cannot change it — rather than being handed a form
  // the server would refuse.
  const usable = usableOrgCapabilities(held, { mfaSatisfied: viewer.mfa.satisfiedForSession });
  const canManageMembers = usable.includes('ORG_MEMBER_MANAGE');
  const canManageRoles = usable.includes('ORG_ROLE_MANAGE');
  const blockedBySecondFactor = !canManageMembers && held.includes('ORG_MEMBER_MANAGE');

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoles, setInviteRoles] = useState<OrgRole[]>(['EVENT_MANAGER']);
  const [issuedToken, setIssuedToken] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function invite(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setIssuedToken(null);
    try {
      const result = await api.request<{ invitationToken?: string }>(`${base}/members`, {
        method: 'POST',
        body: { email: inviteEmail, roles: inviteRoles },
      });
      setInviteEmail('');
      setIssuedToken(result.invitationToken ?? null);
      reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function setRoles(membershipId: string, roles: OrgRole[]): Promise<void> {
    setError(null);
    try {
      await api.request(`${base}/members/${membershipId}/roles`, {
        method: 'PUT',
        body: { roles },
      });
      reload();
    } catch (caught) {
      setError(caught);
    }
  }

  async function remove(membershipId: string): Promise<void> {
    setError(null);
    try {
      await api.request(`${base}/members/${membershipId}`, { method: 'DELETE' });
      reload();
    } catch (caught) {
      setError(caught);
    }
  }

  if (resource.status === 'loading') return <LoadingRegion label="Loading team" />;
  if (resource.status === 'failed') return <FailureNotice error={resource.error} />;

  return (
    <div className="gc-stack gc-stack--loose">
      <FailureNotice error={error} />

      <Card title="Team" eyebrow="Who can do what" flush>
        <div className="gc-table-scroll" style={{ border: 0, borderRadius: 0 }}>
          <table className="gc-table">
            <thead>
              <tr>
                <th scope="col">Person</th>
                <th scope="col">Status</th>
                <th scope="col">Roles</th>
                {canManageRoles && <th scope="col">Change</th>}
                {canManageMembers && (
                  <th scope="col">
                    <span className="gc-visually-hidden">Remove</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {resource.data.members.map((member) => {
                const status = presentation(MEMBERSHIP_STATES, member.status);
                const isSelf = member.userId === viewer.userId;
                return (
                  <tr key={member.membershipId}>
                    <td>
                      <div className="gc-stack gc-stack--tight">
                        <strong>{member.displayName ?? '—'}</strong>
                        <span className="gc-muted">{member.email}</span>
                      </div>
                    </td>
                    <td>
                      <Status
                        tone={status.tone}
                        glyph={status.glyph}
                        label={status.label}
                        title={status.meaning}
                      />
                    </td>
                    <td>
                      {member.roles.length === 0 ? (
                        <span className="gc-muted">None</span>
                      ) : (
                        member.roles.map((role) => ORG_ROLE_LABELS[role]).join(', ')
                      )}
                    </td>
                    {canManageRoles && (
                      <td>
                        <select
                          className="gc-select"
                          aria-label={`Role for ${member.email}`}
                          value={member.roles[0] ?? ''}
                          onChange={(event) =>
                            void setRoles(member.membershipId, [event.target.value as OrgRole])
                          }
                        >
                          {ORG_ROLES.map((role) => (
                            <option key={role} value={role}>
                              {ORG_ROLE_LABELS[role]}
                            </option>
                          ))}
                        </select>
                      </td>
                    )}
                    {canManageMembers && (
                      <td>
                        {/*
                          Self-removal is refused by the server. Not offering the
                          button here means the person is not invited to try
                          something that will be refused.
                        */}
                        {!isSelf && (
                          <Button
                            variant="destructive"
                            onClick={() => void remove(member.membershipId)}
                          >
                            Remove
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {canManageMembers ? (
        <Card title="Invite someone" eyebrow="New member">
          <form className="gc-stack" onSubmit={(event) => void invite(event)}>
            <Field
              label="Email address"
              type="email"
              name="email"
              required
              hint="They must accept from an account using this exact address."
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
            />

            <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="gc-field__label" style={{ marginBottom: '0.5rem' }}>
                Roles
              </legend>
              <div className="gc-grid">
                {ORG_ROLES.map((role) => (
                  <label key={role} className="gc-checkbox">
                    <input
                      type="checkbox"
                      checked={inviteRoles.includes(role)}
                      onChange={(event) =>
                        setInviteRoles((current) =>
                          event.target.checked
                            ? [...current, role]
                            : current.filter((held) => held !== role),
                        )
                      }
                    />
                    <span className="gc-checkbox__body">
                      <span className="gc-checkbox__title">{ORG_ROLE_LABELS[role]}</span>
                      <span className="gc-muted">{ORG_ROLE_DESCRIPTIONS[role]}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <Button
              type="submit"
              variant="primary"
              busy={busy}
              disabled={inviteRoles.length === 0 || inviteEmail.length === 0}
            >
              Send invitation
            </Button>
          </form>

          {issuedToken !== null && (
            <div style={{ marginTop: '1rem' }}>
              <Notice tone="caution" title="Pass this code on yourself">
                Messaging is not live in this release, so nothing was sent. Give this code to the
                person you invited — it only works for the address you entered.
                <div className="gc-mono" style={{ marginTop: '0.5rem', wordBreak: 'break-all' }}>
                  {issuedToken}
                </div>
              </Notice>
            </div>
          )}
        </Card>
      ) : blockedBySecondFactor ? (
        <Notice tone="caution" title="This session cannot change the team">
          You have the capability, but managing members requires a second factor and this session
          did not present one. Enrol an authenticator app under Security, then sign in again.
        </Notice>
      ) : (
        <Notice tone="info" title="You can see the team but not change it">
          Inviting and removing members needs the member-management capability, which your role in
          this organization does not include.
        </Notice>
      )}
    </div>
  );
}
